import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, Wrench, MessageSquare, ChevronDown, ChevronUp,
  Send, Zap, CheckCircle2, AlertTriangle, RefreshCw, Cpu,
} from 'lucide-react';
import { api } from '../../lib/api';

/**
 * AgentAnalysisTab — Session B UI for the agentic tool-calling analyzer.
 *
 * Shows:
 *   1. Verdict + confidence + iris_class chip (institutional view)
 *   2. Tools consulted (expandable trace: tool → args → result)
 *   3. Evidence citations (each claim linked to the tool that produced it)
 *   4. Chat panel: "Ask Iris a follow-up" with tool-augmented answers
 */
const VERDICT_STYLES = {
  UNDERPRICED_FLIP: {
    color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200',
    label: 'Underpriced Flip', icon: Zap,
  },
  STRONG_HOLD: {
    color: 'text-sky-700', bg: 'bg-sky-50', border: 'border-sky-200',
    label: 'Strong Hold', icon: CheckCircle2,
  },
  MOMENTUM: {
    color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200',
    label: 'Momentum Play', icon: Sparkles,
  },
  AVOID: {
    color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200',
    label: 'Avoid', icon: AlertTriangle,
  },
  INSUFFICIENT_DATA: {
    color: 'text-slate-700', bg: 'bg-slate-100', border: 'border-slate-300',
    label: 'Insufficient Data', icon: AlertTriangle,
  },
};

const CLASS_LABELS = {
  residential_secondary: 'Residential (Secondary)',
  residential_offplan: 'Residential (Off-plan)',
  plot: 'Plot / Land',
  commercial: 'Commercial',
  commercial_plot: 'Industrial Land',
  hospitality: 'Hospitality',
  special: 'Special',
};

export default function AgentAnalysisTab({ source, sourceId }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null); // {iris_class, iris_analysis}
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/iris/agent/analysis/${source}/${sourceId}`);
      setData(r.data);
      setError(null);
    } catch (e) {
      if (e.response?.status === 404) setError('not_found');
      else setError(e.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  };

  useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [source, sourceId]);

  const triggerFresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post(`/iris/agent/analyze/${source}/${sourceId}?force=true`);
      if (r.data.cached) {
        setData({ ...(data || {}), iris_analysis: r.data.analysis });
        toast.success('Loaded cached analysis');
      } else {
        toast.success('Fresh analysis queued — will complete in ~90 seconds', { duration: 6000 });
        // Poll for completion
        let tries = 0;
        const poll = setInterval(async () => {
          tries += 1;
          try {
            const rr = await api.get(`/iris/agent/analysis/${source}/${sourceId}`);
            const fp = rr.data.iris_analysis?.input_fingerprint;
            const prevFp = data?.iris_analysis?.input_fingerprint;
            // We forced a re-run — treat any change to analyzed_at as fresh done
            const newTs = rr.data.iris_analysis?.analyzed_at;
            const prevTs = data?.iris_analysis?.analyzed_at;
            if (newTs && newTs !== prevTs) {
              clearInterval(poll);
              setData(rr.data);
              setRefreshing(false);
              toast.success('Fresh analysis ready');
            } else if (tries >= 40) {
              clearInterval(poll);
              setRefreshing(false);
              toast.error('Timed out — try again shortly');
            }
          } catch {/* keep polling */}
        }, 3000);
        return;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to refresh');
    } finally {
      // Only clear if we finished synchronously (cached hit)
      if (data?.iris_analysis) setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="py-16 text-center" data-testid="agent-tab-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#679286] mx-auto"/>
        <div className="text-sm text-slate-500 mt-3">Loading agentic analysis…</div>
      </div>
    );
  }

  const analysis = data?.iris_analysis;
  if (error === 'not_found' || !analysis) {
    return (
      <div className="py-12 text-center" data-testid="agent-tab-empty">
        <Cpu className="w-8 h-8 text-slate-300 mx-auto mb-2"/>
        <div className="text-base font-semibold text-slate-800">No agentic analysis yet</div>
        <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
          Iris can run a deep tool-calling analysis on this listing — Claude will
          consult DLD comps, developer history, and market signals to form a
          citable verdict.
        </div>
        <button
          onClick={triggerFresh}
          disabled={refreshing}
          data-testid="agent-tab-trigger"
          className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1A2530] text-white text-sm font-semibold hover:bg-[#282828] disabled:opacity-50"
        >
          {refreshing ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>}
          {refreshing ? 'Running…' : 'Run agentic analysis'}
        </button>
        <div className="text-xs text-slate-400 mt-2">Takes ~90 seconds. Cached after first run.</div>
      </div>
    );
  }

  return (
    <div data-testid="agent-tab-body" className="space-y-4">
      <VerdictHero analysis={analysis} irisClass={data.iris_class}
                   refreshing={refreshing} onRefresh={triggerFresh}/>
      <ClaimsGrid analysis={analysis}/>
      <ToolTrace analysis={analysis}/>
      <EvidenceCitations analysis={analysis}/>
      <ChatPanel source={source} sourceId={sourceId} analysis={analysis}/>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Verdict hero card
// ═══════════════════════════════════════════════════════════════
function VerdictHero({ analysis, irisClass, refreshing, onRefresh }) {
  const style = VERDICT_STYLES[analysis.verdict] || VERDICT_STYLES.INSUFFICIENT_DATA;
  const Icon = style.icon;
  const score = analysis.hotness_score;
  const conf = analysis.confidence_pct;
  return (
    <div className={`rounded-3xl border-2 ${style.border} ${style.bg} p-5`} data-testid="agent-verdict-hero">
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-full grid place-items-center ${style.color} bg-white/70 shrink-0`}>
          <Icon size={20}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-widest font-bold ${style.color}`}>
              Agentic Verdict
            </span>
            {irisClass && (
              <span data-testid="agent-class-chip"
                className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-white/80 text-slate-700 border border-slate-200">
                {CLASS_LABELS[irisClass] || irisClass}
              </span>
            )}
          </div>
          <div className={`font-serif text-2xl mt-1 ${style.color}`} data-testid="agent-verdict-label">
            {style.label}
          </div>
          <div className="text-sm text-slate-700 mt-2">
            {analysis.primary_case}
          </div>
        </div>
        <button onClick={onRefresh} disabled={refreshing}
          data-testid="agent-refresh"
          title="Re-run agentic analysis"
          className="p-2 rounded-full hover:bg-white/60 text-slate-500 disabled:opacity-50 shrink-0">
          {refreshing ? <Loader2 size={16} className="animate-spin"/> : <RefreshCw size={16}/>}
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-4">
        <StatTile label="Hotness" value={score != null ? `${score}` : '—'} accent={style.color}/>
        <StatTile label="Confidence" value={conf != null ? `${conf}%` : '—'} accent={style.color}/>
        <StatTile label="Tools used" value={analysis.tools_consulted?.length || 0} accent={style.color}/>
      </div>
    </div>
  );
}

function StatTile({ label, value, accent }) {
  return (
    <div className="rounded-xl bg-white/70 border border-white p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`font-serif text-2xl mt-0.5 ${accent}`}>{value}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Positives / Risks
// ═══════════════════════════════════════════════════════════════
function ClaimsGrid({ analysis }) {
  const positives = analysis.positives || [];
  const risks = analysis.risks || [];
  if (!positives.length && !risks.length) return null;
  return (
    <div className="grid md:grid-cols-2 gap-3">
      {positives.length > 0 && (
        <div className="rounded-2xl p-4 bg-emerald-50/60 border border-emerald-100" data-testid="agent-positives">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold mb-2 flex items-center gap-1">
            <CheckCircle2 size={11}/> What Iris found
          </div>
          <ul className="space-y-1.5">
            {positives.map((p, i) => (
              <li key={i} className="text-sm text-emerald-900 flex gap-2">
                <span className="text-emerald-500 mt-1">•</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {risks.length > 0 && (
        <div className="rounded-2xl p-4 bg-amber-50/60 border border-amber-100" data-testid="agent-risks">
          <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold mb-2 flex items-center gap-1">
            <AlertTriangle size={11}/> Risks Iris flagged
          </div>
          <ul className="space-y-1.5">
            {risks.map((r, i) => (
              <li key={i} className="text-sm text-amber-900 flex gap-2">
                <span className="text-amber-500 mt-1">•</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Tool trace — expandable
// ═══════════════════════════════════════════════════════════════
function ToolTrace({ analysis }) {
  const [open, setOpen] = useState(false);
  const trace = analysis.tool_trace || [];
  const tools = analysis.tools_consulted || [];
  if (!trace.length) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white/80" data-testid="agent-tool-trace">
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="agent-trace-toggle"
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50/50 transition"
      >
        <div className="w-9 h-9 rounded-full bg-slate-100 grid place-items-center text-slate-700 shrink-0">
          <Wrench size={16}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">
            Claude consulted {tools.length} tool{tools.length === 1 ? '' : 's'} · {trace.length} call{trace.length === 1 ? '' : 's'}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {tools.slice(0, 4).join(' · ')}{tools.length > 4 ? ` · +${tools.length - 4} more` : ''}
          </div>
        </div>
        {open ? <ChevronUp size={16} className="text-slate-400"/> : <ChevronDown size={16} className="text-slate-400"/>}
      </button>
      {open && (
        <div className="border-t border-slate-100 p-4 space-y-2 max-h-96 overflow-y-auto">
          {trace.map((t, i) => (
            <div key={i} className="rounded-xl border border-slate-100 bg-slate-50/50 p-3" data-testid={`agent-trace-step-${i}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-semibold text-slate-500">STEP {t.step}</span>
                <span className={`text-xs font-mono ${t.ok ? 'text-emerald-700' : 'text-red-700'}`}>
                  {t.tool}
                </span>
                <span className="ml-auto text-[10px] text-slate-400">{t.ms}ms</span>
              </div>
              {t.reasoning && <div className="text-xs text-slate-600 italic mb-1">"{t.reasoning}"</div>}
              {t.args && Object.keys(t.args).length > 0 && (
                <div className="text-[11px] font-mono text-slate-600 bg-white/70 rounded px-2 py-1 mt-1">
                  {JSON.stringify(t.args)}
                </div>
              )}
              {t.result_summary && (
                <div className="text-[11px] font-mono text-slate-500 bg-white/60 rounded px-2 py-1 mt-1 overflow-x-auto">
                  {typeof t.result_summary === 'object' ? JSON.stringify(t.result_summary) : String(t.result_summary)}
                </div>
              )}
              {t.error && (
                <div className="text-[11px] text-red-600 mt-1">Error: {t.error}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Evidence citations
// ═══════════════════════════════════════════════════════════════
function EvidenceCitations({ analysis }) {
  const cites = analysis.evidence_citations || [];
  if (!cites.length) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white/80 p-4" data-testid="agent-citations">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3 flex items-center gap-1">
        <Sparkles size={11}/> Evidence Citations
      </div>
      <div className="space-y-2">
        {cites.map((c, i) => (
          <div key={i} className="text-sm text-slate-800 bg-slate-50/70 rounded-xl p-3 border border-slate-100">
            <div className="text-slate-900">{c.claim}</div>
            <div className="text-[11px] text-slate-500 mt-1 font-mono">
              via <span className="text-[#679286] font-semibold">{c.supported_by_tool}</span>
            </div>
            {c.key_data_points && Object.keys(c.key_data_points).length > 0 && (
              <div className="text-[11px] font-mono text-slate-500 bg-white/70 rounded px-2 py-1 mt-1.5 overflow-x-auto">
                {JSON.stringify(c.key_data_points)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Chat panel — follow-up Q&A with Iris
// ═══════════════════════════════════════════════════════════════
const STARTER_QUESTIONS = [
  'What if the developer defaults?',
  'How does this compare to Emirates Living?',
  "What's the 5-year rental yield trajectory?",
  'What would make you upgrade this to STRONG_HOLD?',
];

function ChatPanel({ source, sourceId, analysis }) {
  const [history, setHistory] = useState([]); // [{role, content, tools?, followups?}]
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [history, busy]);

  const send = async (msg) => {
    const text = (msg ?? input).trim();
    if (!text || busy) return;
    setInput('');
    const nextHist = [...history, { role: 'user', content: text }];
    setHistory(nextHist);
    setBusy(true);
    try {
      const r = await api.post(`/iris/agent/chat/${source}/${sourceId}`, {
        message: text,
        history: nextHist.slice(-6),
      });
      setHistory([...nextHist, {
        role: 'assistant',
        content: r.data.reply,
        tools: r.data.tools_used || [],
        followups: r.data.suggested_followups || [],
      }]);
    } catch (e) {
      setHistory([...nextHist, {
        role: 'assistant',
        content: `Sorry — chat failed: ${e.response?.data?.detail || e.message}`,
        error: true,
      }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#679286]/20 bg-gradient-to-br from-[#EEF2F0]/40 to-white p-4" data-testid="agent-chat">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-[#679286] grid place-items-center text-white">
          <MessageSquare size={15}/>
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-900">Chat with Iris</div>
          <div className="text-[11px] text-slate-500">Follow-up questions on this listing — Iris will re-call tools live</div>
        </div>
      </div>

      {history.length === 0 && (
        <div className="mb-3" data-testid="agent-chat-starters">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Try asking</div>
          <div className="flex flex-wrap gap-2">
            {STARTER_QUESTIONS.map((q, i) => (
              <button key={i} onClick={() => send(q)} disabled={busy}
                data-testid={`agent-chat-starter-${i}`}
                className="px-3 py-1.5 rounded-full text-xs bg-white/80 border border-slate-200 text-slate-700 hover:bg-white hover:border-[#679286] transition disabled:opacity-50">
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div ref={scrollerRef} className="space-y-2 max-h-96 overflow-y-auto mb-3 pr-1" data-testid="agent-chat-history">
          {history.map((h, i) => (
            <ChatBubble key={i} turn={h} onFollowup={send} disabled={busy}/>
          ))}
          {busy && (
            <div className="flex items-center gap-2 text-sm text-slate-500 px-3 py-2" data-testid="agent-chat-busy">
              <Loader2 size={13} className="animate-spin"/>
              <span>Iris is thinking…</span>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Ask a follow-up…"
          disabled={busy}
          data-testid="agent-chat-input"
          className="flex-1 px-4 py-2.5 rounded-full bg-white border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-[#679286]/30 disabled:opacity-50"
        />
        <button
          onClick={() => send()}
          disabled={busy || !input.trim()}
          data-testid="agent-chat-send"
          className="w-10 h-10 rounded-full bg-[#1A2530] text-white grid place-items-center disabled:opacity-40 hover:bg-[#282828] transition"
        >
          {busy ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
        </button>
      </div>
    </div>
  );
}

function ChatBubble({ turn, onFollowup, disabled }) {
  if (turn.role === 'user') {
    return (
      <div className="flex justify-end" data-testid="agent-chat-user">
        <div className="max-w-[85%] rounded-2xl rounded-tr-md bg-[#1A2530] text-white text-sm px-4 py-2.5">
          {turn.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start" data-testid="agent-chat-assistant">
      <div className="max-w-[85%]">
        <div className={`rounded-2xl rounded-tl-md text-sm px-4 py-2.5 whitespace-pre-wrap ${
          turn.error ? 'bg-red-50 border border-red-200 text-red-900' : 'bg-white border border-slate-200 text-slate-800'
        }`}>
          {turn.content}
        </div>
        {(turn.tools?.length > 0) && (
          <div className="mt-1.5 flex flex-wrap gap-1 pl-1">
            {turn.tools.map((t, i) => (
              <span key={i} data-testid="agent-chat-tool-chip"
                className="text-[10px] font-mono text-[#679286] bg-white/80 border border-[#679286]/20 rounded-full px-2 py-0.5">
                <Wrench size={9} className="inline mr-1"/> {t}
              </span>
            ))}
          </div>
        )}
        {(turn.followups?.length > 0) && (
          <div className="mt-2 flex flex-wrap gap-1.5 pl-1">
            {turn.followups.slice(0, 3).map((f, i) => (
              <button key={i} onClick={() => onFollowup(f)} disabled={disabled}
                data-testid="agent-chat-followup"
                className="text-[11px] px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-50">
                {f}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
